<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import axios from 'axios'
import {useHead} from '@vueuse/head'
import {useRoute, useRouter} from 'vue-router'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import VideoCard from '@/components/VideoCard.vue'
import {useI18n} from '@/i18n'

const route = useRoute()
const router = useRouter()
const {t} = useI18n()
const videos = ref([])
const count = ref(0)
const loading = ref(true)
const current = computed(() => Number(route.query.page) || 1)
const activeCategory = computed(() => route.query.category || '')
const categories = ref([])
const pageCount = computed(() => Math.max(1, Math.ceil(count.value / 12)))

async function loadVideos() {
  loading.value = true
  try {
    const response = await axios.get('/api/videos/', {
      params: {page: current.value, ...(activeCategory.value ? {category: activeCategory.value} : {})}
    })
    videos.value = response.data.results || response.data
    count.value = response.data.count || videos.value.length
  } finally {
    loading.value = false
  }
}

function setCategory(category) {
  router.push({query: category ? {category} : {}})
}

function setPage(page) {
  if (page < 1 || page > pageCount.value) return
  router.push({query: {...route.query, page}})
}

onMounted(() => {
  const core = document.createElement('script')
  core.src = '/js/core.min.js'
  core.onload = () => {
    const template = document.createElement('script')
    template.src = '/js/script.js'
    document.body.appendChild(template)
  }
  document.body.appendChild(core)
  loadVideos()
  axios.get('/api/video-categories/').then((response) => {
    categories.value = (response.data.results || response.data).map((category) => ({
      site: category.site,
      name: category.category
    }))
  })
  window.scrollTo({top: 0})
})
watch(() => route.query, loadVideos)

useHead({title: () => `${t('page.videos')} | TPA Housing`, meta: [{name: 'description', content: t('home.videoIntro')}]})
</script>

<template>
  <div class="page">
    <HomeHeader/>
    <section class="video-page-hero"
             style="background-image: linear-gradient(90deg, rgba(8, 23, 38, 0.92), rgba(8, 23, 38, 0.58)), url('/media/video/covers/steel-warehouse-02.png')">
      <div class="container">
        <span class="video-eyebrow">TPA HOUSING</span>
        <h1>{{ t('page.videos') }}</h1>
        <p>{{ t('home.videoIntro') }}</p>
      </div>
    </section>

    <main class="section section-lg bg-default video-library">
      <div class="container">
        <div v-if="categories.length" class="video-filters" :aria-label="t('common.category')">
          <button :class="{active: !activeCategory}" @click="setCategory('')">{{ t('common.all') }}</button>
          <button v-for="category in categories" :key="category.site"
                  :class="{active: activeCategory === category.site}" @click="setCategory(category.site)">
            {{ category.name }}
          </button>
        </div>
        <div v-if="loading" class="video-state">{{ t('common.loading') }}</div>
        <div v-else-if="videos.length" class="video-grid">
          <VideoCard v-for="video in videos" :key="video.id" :video="video"/>
        </div>
        <div v-else class="video-state">{{ t('common.noVideos') }}</div>
        <nav v-if="pageCount > 1" class="video-pagination" aria-label="Pagination">
          <button :disabled="current === 1" @click="setPage(current - 1)" aria-label="Previous">
            <i class="mdi mdi-chevron-left"></i>
          </button>
          <button v-for="page in pageCount" :key="page" :class="{active: current === page}" @click="setPage(page)">{{ page }}</button>
          <button :disabled="current === pageCount" @click="setPage(current + 1)" aria-label="Next">
            <i class="mdi mdi-chevron-right"></i>
          </button>
        </nav>
      </div>
    </main>
    <Footer/>
  </div>
</template>
